import type { Dayjs } from 'dayjs';
import type { CreateAssetPayload } from '~/app/services/asset/asset.service';
import type { EvseType } from '@evimo/types/enums/evse-type';

/** Form fields needed to build the asset create API payload (no plan fields). */
export type CreateAssetPayloadSource = {
  assetName: string;
  serialNumber: string;
  manufacturer?: string;
  siteId: string;
  zoneId?: string;
  assetTypeId: string;
  assetModelId?: string;
  standard?: EvseType | null;
  parentAssetId?: string;
  installedAt?: Dayjs | null;
  commissionedAt?: Dayjs | null;
  decommissionedAt?: Dayjs | null;
  expectedDailyEarning?: number | null;
  landlordOrganizationId?: string;
};

export function shouldAttemptPlanAssign(
  planId: string | null | undefined,
): boolean {
  return Boolean(planId?.trim());
}

export function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export function buildCreateAssetPayload(
  values: CreateAssetPayloadSource,
): CreateAssetPayload {
  return {
    assetName: values.assetName,
    serialNumber: values.serialNumber.trim(),
    manufacturer: values.manufacturer,
    siteId: values.siteId,
    zoneId: values.zoneId,
    assetTypeId: values.assetTypeId,
    assetModelId: values.assetModelId,
    standard: values.standard ?? null,
    parentAssetId: values.parentAssetId,
    installedAt: values.installedAt?.toISOString(),
    commissionedAt: values.commissionedAt?.toISOString(),
    decommissionedAt: values.decommissionedAt?.toISOString(),
    expectedDailyEarning:
      values.expectedDailyEarning != null
        ? Number(values.expectedDailyEarning)
        : 0,
    landlordOrganizationId: values.landlordOrganizationId ?? null,
  };
}

export function resolveAssignStartDate(
  startDate: Dayjs | null | undefined,
): Date {
  if (startDate?.isValid()) {
    return startDate.toDate();
  }
  return new Date();
}

export function toPlanSelectOptions(
  plans: Array<{ id: string; name: string; code: string; active: boolean }>,
): Array<{ value: string; label: string }> {
  return plans
    .filter((plan) => plan.active)
    .map((plan) => ({
      value: plan.id,
      label: `${plan.code} — ${plan.name}`,
    }));
}

export type PostCreateAction =
  | { kind: 'doneWithoutAssign' }
  | { kind: 'assign'; planId: string };

/**
 * After a successful asset create, decide whether Step 2 (assign) should run.
 * Create failure must never call this — callers stop before reaching here.
 */
export function resolvePostCreateAction(args: {
  planId: string | null | undefined;
  canAssignPlan: boolean;
}): PostCreateAction {
  if (!args.canAssignPlan || !shouldAttemptPlanAssign(args.planId)) {
    return { kind: 'doneWithoutAssign' };
  }
  return { kind: 'assign', planId: args.planId!.trim() };
}

/**
 * Retry only runs assign for an already-created asset — never create again.
 */
export function canRetryPlanAssign(args: {
  createdAssetId: string | null | undefined;
  planId: string | null | undefined;
}): boolean {
  return (
    Boolean(args.createdAssetId?.trim()) && shouldAttemptPlanAssign(args.planId)
  );
}
